import { Heading } from 'src/components/Heading';
import { BaseRow, Importer, ImporterField } from 'react-csv-importer';

import 'react-csv-importer/dist/index.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import useAuthedRequest, { AuthedRequest } from 'src/hooks/useAuthedRequest';
import { formatPhoneNumberWithError } from 'src/utils/number';
import { SlidersHorizontal, WarningCircle } from '@phosphor-icons/react';
import {
  Agent,
  getReferencedContextParams,
} from 'src/interfaces/agent.interface';
import { useNotification } from 'src/contexts/NotificationContext';
import { Loading } from 'src/components/Loading';
import { Checkbox } from 'src/components/Checkbox';
import {
  getCampaignAgentIds,
  getCampaignContextParams,
} from 'src/services/campaigns/campaigns.service';
import { ExecutionDeduplicationAction } from 'src/interfaces/workflows.interface';
import { useCampaigns } from 'src/hooks/useCampaigns';
import {
  ADD_CONTACTS_MAX_BATCH_SIZE,
  Contact,
} from 'src/services/campaigns/campaigns.interface';
import { processPhoneNumber } from 'src/utils/number';
import { CardWithBorder } from 'src/components/CardWithBorder';
import clsx from 'clsx';
import { getSafeReturnToPath } from 'src/utils/returnTo';

interface ImportError {
  rowNumber: number;
  message: string;
  type: 'error' | 'warning';
}

type Field = {
  name: string;
  label: string;
};

const TO_EMAIL_KEY = 'to_email';
const TO_EMAIL_OPTION: Field = {
  name: TO_EMAIL_KEY,
  label: 'To Email',
};

const TO_NUMBER_KEY = 'to_number';
const DEFAULT_REQUIRED_PARAMS: Field[] = [
  { name: TO_NUMBER_KEY, label: 'To Number' },
];

const fetchAgentsById = async (
  agentIds: string[],
  authedRequest: AuthedRequest,
): Promise<Agent[]> => {
  return Promise.all(
    agentIds.map((agentId) => authedRequest.get(`/agents/${agentId}`)),
  );
};

type BaseRowWithRowNumber = {
  row: BaseRow;
  rowNumber: number;
};

export const AddContactsFromCsv = () => {
  const authedRequest = useAuthedRequest();
  const campaignsService = useCampaigns();
  const { id: campaignReferenceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [importErrors, setImportErrors] = useState<ImportError[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [allContextParams, setAllContextParams] = useState<string[]>([]);
  const [campaignExecutionParams, setCampaignExecutionParams] = useState<
    Field[]
  >([]);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const notification = useNotification();
  const hasInitiatedLoad = useRef(false);
  const batchAddErrorRef = useRef(false); // Need this because react-csv-importer doesn't respect React lifecycle events

  useEffect(() => {
    if (!campaignReferenceId || hasInitiatedLoad.current) return;
    hasInitiatedLoad.current = true;

    const doLoad = async () => {
      try {
        const fetchedCampaign =
          await campaignsService.fetchCampaign(campaignReferenceId);

        // Extract all the agents referenced in the campaign
        const agentIds = getCampaignAgentIds(fetchedCampaign);
        const agents = await fetchAgentsById(agentIds, authedRequest);

        // Compute all the context params that are referenced in the campaign
        let contextParams = new Array<string>();

        agents.forEach((agent) => {
          const referencedContextParams = getReferencedContextParams(agent);
          contextParams.push(...referencedContextParams);
        });
        const campaignContextParams = getCampaignContextParams(fetchedCampaign);

        contextParams = Array.from(
          new Set([...contextParams, ...campaignContextParams]),
        );

        setAllContextParams(contextParams.sort());

        if (fetchedCampaign.emailCampaignConfig) {
          setCampaignExecutionParams([TO_EMAIL_OPTION]);
        }
      } catch (err) {
        console.error(err);
        notification.error('Failed to load campaign');
      } finally {
        setIsInitialized(true);
      }
    };

    doLoad();
  }, [campaignReferenceId, authedRequest, notification, campaignsService]);

  const dataHandler = useCallback(
    async (rows: BaseRow[], info: { startIndex: number }) => {
      // Skip processing if there's already a batch error
      if (batchAddErrorRef.current) {
        return;
      }

      const validRows: BaseRowWithRowNumber[] = [];
      const errors: ImportError[] = [];

      for (let index = 0; index < rows.length; index++) {
        const row = rows[index];
        const { number, error } = formatPhoneNumberWithError(
          row[TO_NUMBER_KEY] as string,
        );
        if (error) {
          errors.push({
            rowNumber: info.startIndex + index + 1,
            message: `Invalid phone number: ${row[TO_NUMBER_KEY]}`,
            type: 'error',
          });
          continue;
        }
        validRows.push({
          row: {
            ...row,
            [TO_NUMBER_KEY]: number,
          },
          rowNumber: info.startIndex + index + 1,
        });
      }

      if (validRows.length === 0) {
        setImportErrors((prev) => [...prev, ...errors]);
        return;
      }

      for (let i = 0; i < validRows.length; i += ADD_CONTACTS_MAX_BATCH_SIZE) {
        // Skip remaining batches if there's already an error
        if (batchAddErrorRef.current) {
          break;
        }

        const batch = validRows.slice(i, i + ADD_CONTACTS_MAX_BATCH_SIZE);
        const contacts: Contact[] = batch.map(
          (rowWithRowNumber: BaseRowWithRowNumber) => {
            const context = allContextParams.reduce(
              (acc, param) => {
                acc[param] = (rowWithRowNumber.row[param] as string) || '';
                return acc;
              },
              {} as Record<string, string>,
            );

            return {
              toNumber: rowWithRowNumber.row[TO_NUMBER_KEY] as string,
              toEmail: rowWithRowNumber.row[TO_EMAIL_KEY] as string,
              context,
            };
          },
        );

        const startTime = Date.now();
        try {
          const batchAddResult =
            await campaignsService.batchAddContactsToCampaign({
              referenceId: campaignReferenceId as string,
              contacts,
              deduplicate: skipDuplicates,
            });
          for (
            let resultIndex = 0;
            resultIndex < batchAddResult.executions.length;
            resultIndex++
          ) {
            const result = batchAddResult.executions[resultIndex];
            const rowWithRowNumber = validRows[i + resultIndex];

            if (result.error) {
              // ERROR case - always treat as error, never as duplicate
              console.error(
                `Unexpected error adding contact from row ${rowWithRowNumber.rowNumber}: ${result.error}`,
              );
              errors.push({
                rowNumber: rowWithRowNumber.rowNumber,
                message: 'Unexpected error adding contact',
                type: 'error',
              });
            } else if (
              result.duplicateResolutionAction ===
              ExecutionDeduplicationAction.SKIP
            ) {
              // SKIP case - duplicate was skipped
              errors.push({
                rowNumber: rowWithRowNumber.rowNumber,
                message:
                  `Duplicate contact skipped - contact already exists in campaign with phone number ` +
                  `${processPhoneNumber(rowWithRowNumber.row[TO_NUMBER_KEY] as string)}`,
                type: 'warning',
              });
            }
            // UPDATE and INSERT cases don't need error handling
          }
        } catch (err) {
          console.error('batchAddContactsToCampaign error: ', err);
          batchAddErrorRef.current = true;
          notification.error(
            'Failed to add contacts to campaign. Please try again.',
          );
          break;
        }

        const elapsedTime = Date.now() - startTime;
        // Only pause if the call took less than 1 second to avoid load balancer errors
        if (elapsedTime < 1000) {
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 - elapsedTime),
          );
        }
      }

      errors.sort((a, b) => a.rowNumber - b.rowNumber);
      setImportErrors((prev) => [...prev, ...errors]);
    },
    [
      campaignReferenceId,
      allContextParams,
      campaignsService,
      notification,
      skipDuplicates,
    ],
  );

  const handleBackToCampaign = () => {
    const returnTo = searchParams.get('returnTo');
    navigate(getSafeReturnToPath(returnTo, '/campaigns'));
  };

  const clearErrors = () => {
    setImportErrors([]);
    batchAddErrorRef.current = false;
  };

  return (
    <>
      <Heading
        title="Add Contacts from CSV"
        subtitle="Add contacts to your campaign from a CSV file."
        backAction={handleBackToCampaign}
      />
      <div className="p-6 space-y-6">
        {!isInitialized ? (
          <div className="flex gap-2 items-center justify-center py-12">
            <Loading size="8" />
            <p className="text-neutral-400">Loading...</p>
          </div>
        ) : (
          <>
            {/* CSV Upload Section */}
            <CardWithBorder className="space-y-4">
              <div>
                <h3 className="text-h3 font-h3 text-primary-h3 mb-2">
                  Upload CSV File
                </h3>
                <p className="text-description text-neutral-500">
                  Select a CSV file containing your contacts.
                </p>
              </div>

              <Importer
                dataHandler={dataHandler}
                defaultNoHeader={false}
                restartable={true}
                onStart={clearErrors}
              >
                {DEFAULT_REQUIRED_PARAMS.map((importField) => (
                  <ImporterField
                    key={importField.name}
                    name={importField.name}
                    label={importField.label}
                    optional={false}
                  />
                ))}
                {campaignExecutionParams.map((param) => (
                  <ImporterField
                    key={param.name}
                    name={param.name}
                    label={param.label}
                    optional={false}
                  />
                ))}
                {allContextParams.map((param) => (
                  <ImporterField
                    key={param}
                    name={param}
                    label={param}
                    optional={true}
                  />
                ))}
              </Importer>

              {/* Error Display */}
              {importErrors.length > 0 && (
                <CardWithBorder className="bg-neutral-50">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center">
                      <WarningCircle className="h-6 w-6 text-primary mr-2" />
                      <h4>Some Contacts Were Skipped</h4>
                    </div>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={clearErrors}
                      className="text-sm"
                    >
                      Clear Errors
                    </Button>
                  </div>
                  <ul className="max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-200">
                    {importErrors.map((err) => (
                      <li
                        key={err.rowNumber}
                        className={clsx(
                          'flex items-start space-x-2 text-sm p-1 rounded',
                          err.type === 'error'
                            ? 'text-error-500'
                            : 'text-warning-600',
                        )}
                      >
                        <span className="font-mono flex-shrink-0 font-medium">
                          Row {err.rowNumber}:
                        </span>
                        <span>{err.message}</span>
                      </li>
                    ))}
                  </ul>
                </CardWithBorder>
              )}
            </CardWithBorder>

            {/* Import Options */}
            <CardWithBorder className="bg-neutral-50">
              <div className="space-y-4">
                <div className="flex items-center mb-4">
                  <SlidersHorizontal className="h-6 w-6 text-primary mr-2" />
                  <h4>Import Options</h4>
                </div>

                <div className="flex items-start gap-4">
                  <Checkbox
                    checked={skipDuplicates}
                    onChange={setSkipDuplicates}
                    ariaLabel="Skip contacts with duplicate phone numbers"
                    className="mt-1"
                  />
                  <div className="flex-1 space-y-2">
                    <label
                      className="text-body font-medium text-neutral-700 cursor-pointer block"
                      onClick={() => setSkipDuplicates(!skipDuplicates)}
                    >
                      Skip contacts with duplicate phone numbers
                    </label>
                    <p className="text-description text-neutral-500 leading-relaxed">
                      When importing into this campaign, any contact whose phone
                      number already exists in this campaign will be
                      skipped—only unique contacts are added.
                    </p>
                  </div>
                </div>
              </div>
            </CardWithBorder>
          </>
        )}

        {/* Footer Actions */}
        {isInitialized && (
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outlined"
              onClick={handleBackToCampaign}
              size="small"
            >
              Back
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
